import { ToolExecutionState } from 'ducks/chatDrawer'
import { CheckOutlineIcon, keyframes, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useChatScroll } from '../../ChatScrollContext'
import { loadBatteryDesignAssistantResult } from '../../utils/loadBatteryDesignAssistantResult'
import { BaseToolProgressItem } from '../shared/BaseToolProgressItem'
import { BatteryRecommendationsList } from './BatteryRecommendationsList'

interface BatteryDesignAssistantProgressItemProps {
  toolExecutionState: ToolExecutionState
  messageIndex: number
}

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  animation: `${fadeIn} 0.3s ease-out`,
})

export const BatteryDesignAssistantProgressItem: React.FC<BatteryDesignAssistantProgressItemProps> = ({
  toolExecutionState,
  messageIndex,
}) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { scrollToBottom } = useChatScroll()
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const batterySets = toolExecutionState.data?.batterySets || []

  // Scroll to bottom when recommendations appear
  useEffect(() => {
    if (toolExecutionState.status === 'ready' && batterySets.length > 0) {
      // Use a small delay to ensure the DOM has updated
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    }
  }, [toolExecutionState.status, batterySets.length, scrollToBottom])

  const handleLoad = async () => {
    if (selectedIndex === null) {
      return
    }

    try {
      await loadBatteryDesignAssistantResult(selectedIndex)

      // Update status to completed
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex,
        toolExecutionState: {
          ...toolExecutionState,
          status: 'completed',
          description: translate('Battery Added', { _: 'Battery Added' }),
        },
      })
    } catch (error: any) {
      console.error('Error loading battery design assistant result:', error)
      // Update status to error
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex,
        toolExecutionState: {
          ...toolExecutionState,
          status: 'error',
          error: {
            message: error?.message || 'Failed to load battery recommendations',
          },
        },
      })
    }
  }

  // If we have recommendations in ready state, show custom UI with recommendations
  if (toolExecutionState.status === 'ready' && batterySets.length > 0) {
    return (
      <Wrapper>
        <BaseToolProgressItem
          toolExecutionState={toolExecutionState}
          messageIndex={messageIndex}
          runningLabel="Generating battery suggestions..."
          completedLabel="Battery recommendations loaded"
          errorLabel="Battery design assistant failed"
          readyLabel="Ready"
          showCountdown={{ completed: 5, error: 15 }}
          onReadyAction={handleLoad}
          readyActionLabel="Load Batteries"
          readyActionIcon={<CheckOutlineIcon size={16} color="#6B7280" />}
          readyActionDisabled={selectedIndex === null}
          readyActionTooltip={
            selectedIndex === null ? 'Please select a battery option first' : 'Load selected batteries into the system'
          }
        />
        <BatteryRecommendationsList
          batterySets={batterySets}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
        />
      </Wrapper>
    )
  }

  // Otherwise use the base component
  return (
    <BaseToolProgressItem
      toolExecutionState={toolExecutionState}
      messageIndex={messageIndex}
      runningLabel="Generating battery suggestions..."
      completedLabel="Battery recommendations loaded"
      errorLabel="Battery design assistant failed"
      readyLabel="Ready"
      showCountdown={{ completed: 5, error: 15 }}
    />
  )
}
